(function () {
  const SELECTORS = {
    wrapper: '[scroll-bar="wrapper"]',
    section: '[scroll-bar="section"]',
  };

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const MOTION_ATTR = 'data-a11y-motion';
  const MOTION_EVENT = 'gleezy:motion';

  // Motion stops for either reason: the OS preference, or the page's own
  // control in ada.js. That control is what satisfies SC 2.2.2 here — the
  // media query only reaches people who have set the system preference.
  function motionReduced() {
    return reduceMotion.matches
      || document.documentElement.getAttribute(MOTION_ATTR) === 'reduced';
  }

  // Safari < 14 only supports the deprecated addListener API
  function onMediaChange(mql, handler) {
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(handler);
    }
  }

  function initMarquee(wrapper) {
    const originalSection = wrapper.querySelector(SELECTORS.section);
    if (!originalSection) return;

    const speed = parseFloat(wrapper.dataset.speed) || 50;
    const direction = wrapper.dataset.direction === 'right' ? -1 : 1;

    let offset = 0;
    let paused = false;
    let hovered = false;
    let focused = false;
    let lastTime = null;
    let rafId = null;
    let resizeTimer = null;

    // Prevent sections from stealing hover events
    wrapper.style.pointerEvents = 'auto';
    originalSection.style.pointerEvents = 'none';
    originalSection.querySelectorAll('a, button, [role="link"]').forEach((el) => {
      el.style.pointerEvents = 'auto';
    });

    function getAllSections() {
      return wrapper.querySelectorAll(SELECTORS.section);
    }

    function removeClones() {
      wrapper.querySelectorAll('[data-clone]').forEach((el) => el.remove());
    }

    function populateClones() {
      removeClones();

      const sectionWidth = originalSection.offsetWidth;
      if (sectionWidth === 0) return;

      const targetWidth = window.innerWidth + sectionWidth * 2;
      const clonesNeeded = Math.ceil(targetWidth / sectionWidth);

      for (let i = 0; i < clonesNeeded; i++) {
        const clone = originalSection.cloneNode(true);
        clone.setAttribute('data-clone', '');
        // Clones are decorative duplicates: hide them from assistive tech
        // and keep them out of the tab order, but still clickable by mouse.
        clone.setAttribute('aria-hidden', 'true');
        clone.style.pointerEvents = 'none';
        clone.querySelectorAll('a, button, [role="link"]').forEach((el) => {
          el.style.pointerEvents = 'auto';
          el.tabIndex = -1;
        });
        wrapper.appendChild(clone);
      }
    }

    function tick(timestamp) {
      if (!paused) {
        if (lastTime === null) {
          lastTime = timestamp;
          // Skip this frame to avoid a large delta after unpausing
        } else {
          const delta = (timestamp - lastTime) / 1000;
          lastTime = timestamp;

          offset += speed * direction * delta;

          const sectionWidth = originalSection.offsetWidth;
          if (sectionWidth > 0) {
            if (direction === 1 && offset >= sectionWidth) {
              offset -= sectionWidth;
            } else if (direction === -1 && offset <= -sectionWidth) {
              offset += sectionWidth;
            }
          }

          const sections = getAllSections();
          for (let i = 0; i < sections.length; i++) {
            sections[i].style.transform = `translate3d(${-offset}px, 0, 0)`;
          }
        }
      } else {
        lastTime = null;
      }

      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (rafId !== null || motionReduced()) return;
      lastTime = null;
      rafId = requestAnimationFrame(tick);
    }

    function stop() {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      lastTime = null;
      offset = 0;
      getAllSections().forEach((section) => {
        section.style.transform = '';
      });
    }

    function applyMotionPreference() {
      if (motionReduced()) {
        stop();
        removeClones();
      } else {
        populateClones();
        start();
      }
    }

    // Hover and focus each hold the marquee independently, so releasing one
    // while the other still applies must not restart it.
    function syncPaused() {
      const shouldPause = hovered || focused;
      if (shouldPause === paused) return;
      paused = shouldPause;
      if (!paused) {
        lastTime = null;
        start(); // no-op if already running or if motion is reduced
      }
    }

    wrapper.addEventListener('mouseenter', () => {
      hovered = true;
      syncPaused();
    });

    wrapper.addEventListener('mouseleave', () => {
      hovered = false;
      syncPaused();
    });

    // The partner logos are links, so a keyboard user tabs through content that
    // is still sliding. Focus holds it still for as long as they are inside.
    wrapper.addEventListener('focusin', () => {
      focused = true;
      syncPaused();
    });

    wrapper.addEventListener('focusout', (event) => {
      // Moving between two links inside the marquee fires focusout before the
      // next focusin. Without this the marquee would lurch on every tab.
      if (wrapper.contains(event.relatedTarget)) return;
      focused = false;
      syncPaused();
    });

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!motionReduced()) populateClones();
      }, 200);
    });

    onMediaChange(reduceMotion, applyMotionPreference);

    // ada.js owns the on-page control and broadcasts when it is pressed.
    window.addEventListener(MOTION_EVENT, applyMotionPreference);

    applyMotionPreference();
  }

  function init() {
    document.querySelectorAll(SELECTORS.wrapper).forEach(initMarquee);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();